import { Briefcase, PlugZap, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { ProjectCard } from "@/components/persons/ProjectCard";
import { ProjectDetailDialog } from "@/components/persons/ProjectDetailDialog";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { usePersonProjects } from "@/hooks/use-person-projects";
import { formatHours } from "@/lib/persons/project-format";
import {
  currentMonthKey,
  firstUnderGuidedMonth,
  GUIDANCE_HISTORY_MONTHS,
  GUIDANCE_WINDOW_MONTHS,
  type GuidanceMonth,
  isInternalAccount,
  labelWindow,
  monthWindow,
  type PersonProjectAssignment,
  summarizeGuidance,
  UNDER_GUIDED_PERCENT,
} from "@/lib/persons/project-guidance";
import { cn } from "@/lib/utils";

/**
 * The section's subgroups. "All" is every live assignment — client and internal
 * together — and is the default so the tab opens on the full picture. Client
 * and Internal split that; Previous is the rolled-off work, of either kind.
 */
type TabKey = "all" | "client" | "internal" | "previous";

/** What a card leads with — the account, or the demand when there is none. */
function cardHeadline(a: PersonProjectAssignment): string {
  return a.accountName ?? a.demandName;
}

export function PersonProjectsSection({
  personId,
  personName,
}: {
  personId: string;
  personName: string;
}) {
  const [tab, setTab] = useState<TabKey>("all");
  // The month every card reports on. Defaults to the current month, which is
  // the one a manager is answering for; the banner is how it's changed.
  const [selectedMonth, setSelectedMonth] = useState(currentMonthKey);
  const [openAssignment, setOpenAssignment] = useState<PersonProjectAssignment | null>(null);
  const { data, isLoading, isError, error, refetch } = usePersonProjects(personId);

  // `isActiveNow` is computed upstream by NOS from the demand status, the
  // assignment status and today's date — don't re-derive it here.
  const assignments = data?.assignments ?? [];
  const active = assignments.filter((a) => a.isActiveNow);
  const previous = assignments.filter((a) => !a.isActiveNow);
  const internal = active.filter((a) => isInternalAccount(a.accountName));
  const client = active.filter((a) => !isInternalAccount(a.accountName));
  const groups: Record<TabKey, PersonProjectAssignment[]> = {
    all: active,
    client,
    internal,
    previous,
  };
  const shown = groups[tab];
  const groupCounts: Record<TabKey, number> = {
    all: active.length,
    client: client.length,
    internal: internal.length,
    previous: previous.length,
  };

  // History either side of today: the forward months carry the under-guided
  // read, the past months are where logged hours actually exist.
  const window = monthWindow(GUIDANCE_HISTORY_MONTHS, GUIDANCE_WINDOW_MONTHS - 1);
  const summary = summarizeGuidance(active, window);
  const labels = labelWindow(window);
  const selectedLabel = labels.find((l) => l.month === selectedMonth)?.label ?? selectedMonth;

  // An account can run several SOWs at once, so a card headline is not unique.
  // Cards whose headline repeats get the SOW name back to tell them apart.
  const headlineCounts = new Map<string, number>();
  for (const a of shown) {
    const key = cardHeadline(a);
    headlineCounts.set(key, (headlineCounts.get(key) ?? 0) + 1);
  }

  const settled = !isLoading && !isError && data !== undefined;
  const connected = data?.connected ?? false;

  return (
    <div className="space-y-4">
      <DetailSection
        title="Current Projects"
        boldTitle
        className="gap-3 py-4"
        headerActions={
          settled && connected ? (
            <GroupTabs tab={tab} onChange={setTab} counts={groupCounts} />
          ) : null
        }
      >
        {isLoading && <ProjectsSkeleton />}

        {isError && (
          <ErrorState
            message={error instanceof Error ? error.message : "Could not load projects"}
            onRetry={() => {
              void refetch();
            }}
          />
        )}

        {settled && !connected && <NotConnectedState />}

        {settled && connected && (
          <>
            {active.length > 0 && (
              <GuidanceSummary
                months={summary}
                window={window}
                selectedMonth={selectedMonth}
                onSelectMonth={setSelectedMonth}
              />
            )}

            {shown.length === 0 ? (
              <EmptyState
                icon={Briefcase}
                title={EMPTY_COPY[tab].title}
                description={EMPTY_COPY[tab].description(personName)}
              />
            ) : (
              // The grid sits in a recessed well because the section behind it
              // is `bg-card` — which is pure white in the light theme, exactly
              // the cards' own background. Tinting the cards instead doesn't
              // work: `--muted` is #f2f5f9, so any fraction of it over white is
              // invisible. Sinking the container is what actually separates
              // them.
              <ul className="grid gap-3 rounded-xl bg-muted/70 p-3 sm:grid-cols-2 lg:grid-cols-3">
                {shown.map((a) => (
                  <li key={a.id} className="min-w-0">
                    <ProjectCard
                      assignment={a}
                      month={selectedMonth}
                      monthLabel={selectedLabel}
                      showSow={(headlineCounts.get(cardHeadline(a)) ?? 0) > 1}
                      onOpen={() => setOpenAssignment(a)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </DetailSection>

      <ProjectDetailDialog
        assignment={openAssignment}
        open={openAssignment !== null}
        onOpenChange={(next) => {
          if (!next) setOpenAssignment(null);
        }}
      />
    </div>
  );
}

function ProjectsSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-24 w-full" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    </div>
  );
}

/**
 * Shown when the server has no gateway credentials. Deliberately not an error:
 * assignments live in NOS and are read live, so an environment without gateway
 * access simply has nothing to show — nothing is broken and there is nothing
 * for the viewer to retry.
 */
function NotConnectedState() {
  return (
    <EmptyState
      icon={PlugZap}
      title="Not connected to NOS"
      description="Project assignments and guidance are read live from the Nymbl Gateway. This environment doesn't have gateway access configured."
    />
  );
}

/** Tab order and labels. "All" leads because it is the default view. */
const TABS: { key: TabKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "client", label: "Client" },
  { key: "internal", label: "Internal" },
  { key: "previous", label: "Previous" },
];

/** Per-tab empty copy — each subgroup is empty for its own reason. */
const EMPTY_COPY: Record<TabKey, { title: string; description: (name: string) => string }> = {
  all: {
    title: "No active projects",
    description: (name) => `${name} isn't assigned to an open demand right now.`,
  },
  client: {
    title: "No client projects",
    description: (name) => `${name}'s live work is all internal to Nymbl.`,
  },
  internal: {
    title: "No internal projects",
    description: (name) => `${name} isn't on any Nymbl-internal work right now.`,
  },
  previous: {
    title: "No previous projects",
    description: (name) => `Nothing has rolled off for ${name} yet.`,
  },
};

function GroupTabs({
  tab,
  onChange,
  counts,
}: {
  tab: TabKey;
  onChange: (next: TabKey) => void;
  counts: Record<TabKey, number>;
}) {
  return (
    // Every tab renders even at zero, so the control doesn't reflow between
    // one person and the next.
    <div className="inline-flex items-center rounded-lg border p-0.5">
      {TABS.map((t) => (
        <button
          key={t.key}
          type="button"
          aria-pressed={tab === t.key}
          onClick={() => onChange(t.key)}
          className={cn(
            "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
            tab === t.key
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {t.label} ({counts[t.key]})
        </button>
      ))}
    </div>
  );
}

/**
 * Guided capacity across every active assignment, month by month. This is the
 * "is this person under-guided" read, so it is deliberately styled as a banner
 * rather than another card — it summarises the cards below and must not look
 * like one more project among them.
 *
 * It is also the month picker: each cell selects the month the cards report on.
 */
function GuidanceSummary({
  months,
  window,
  selectedMonth,
  onSelectMonth,
}: {
  months: GuidanceMonth[];
  window: string[];
  selectedMonth: string;
  onSelectMonth: (month: string) => void;
}) {
  const current = currentMonthKey();
  // Only the forward months are scanned for the gap, regardless of which month
  // is selected — selecting a past month must not stop a future gap being
  // flagged, and a past month being under-guided is not actionable.
  const gap = firstUnderGuidedMonth(months.filter((m) => m.month >= current));
  const labels = labelWindow(window);

  return (
    <div className="mb-4 rounded-xl border border-primary/25 bg-primary/5 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold">Guided capacity</h4>
          <p className="text-xs text-muted-foreground">
            Guided and logged hours, totalled across all active projects — the last{" "}
            {GUIDANCE_HISTORY_MONTHS} months and the next {GUIDANCE_WINDOW_MONTHS}. Pick a month to
            see it on every project below.
          </p>
        </div>
        {gap && (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-warning-bg px-2 py-1 text-xs font-medium text-warning-foreground">
            <TriangleAlert className="size-3.5" />
            Under-guided from {labels.find((l) => l.month === gap.month)?.label ?? gap.month}
          </span>
        )}
      </div>

      {/* Larger, evenly split columns — this is the headline number, so it gets
          room the compact per-SOW strips deliberately don't. */}
      {/* Each cell is a toggle in its own right — the buttons carry their own
          labels and pressed state, so the wrapper needs no group role. */}
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${labels.length}, 1fr)` }}>
        {labels.map((l) => {
          const entry = months.find((m) => m.month === l.month);
          const pct = entry?.allocationPercent ?? null;
          const isPast = l.month < current;
          const isSelected = l.month === selectedMonth;
          // Past months are history: never flagged, and dimmed so the eye lands
          // on the months that can still be acted on.
          const under = !isPast && (pct ?? 0) < UNDER_GUIDED_PERCENT;
          return (
            <button
              key={l.month}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelectMonth(l.month)}
              className={cn(
                "rounded-lg px-3 py-2 text-left transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isPast ? "bg-background/40" : "bg-background/70",
                // The current month keeps a faint ring for orientation; the
                // selected month gets a solid one so the two never read alike.
                !isSelected && l.month === current && "ring-1 ring-primary/30",
                isSelected
                  ? "bg-background ring-2 ring-primary"
                  : "hover:bg-background/90 hover:ring-1 hover:ring-primary/40",
              )}
            >
              <div className="text-xs font-medium text-muted-foreground">{l.label}</div>
              <div
                className={cn(
                  "mt-0.5 text-xl font-semibold tabular-nums leading-tight",
                  isPast && !isSelected && "text-muted-foreground",
                )}
              >
                {entry?.hours !== null && entry?.hours !== undefined ? (
                  formatHours(entry.hours)
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
              {/* Explicitly labelled: the big number sits under a "Guided
                  capacity" heading and reads as guided, so only the second
                  figure needs naming. */}
              <div className="text-xs tabular-nums text-muted-foreground">
                {entry?.loggedHours !== null && entry?.loggedHours !== undefined
                  ? `${formatHours(entry.loggedHours)} logged`
                  : "none logged"}
              </div>
              <div
                className={cn(
                  "text-xs font-medium tabular-nums",
                  under ? "text-warning-foreground" : "text-muted-foreground",
                )}
              >
                {pct !== null ? `${pct}% of capacity` : "Not guided"}
              </div>
            </button>
          );
        })}
      </div>

      <p className="mt-2 text-xs text-muted-foreground">
        A month guided under {UNDER_GUIDED_PERCENT}% of capacity is flagged, from this month on —
        past months are history.
      </p>
    </div>
  );
}
